<template>
  <v-navigation-drawer
    v-if="!isEtl"
    :expand-on-hover="true"
    permanent
    data-cy="app-navigation-bar"
    rail
    :width="null"
  >
    <v-list
      v-model="currentPage"
      density="compact"
    >
      <v-list-item
        v-for="item in userNavigationLinks"
        :key="item.text"
        data-cy="app-nav-link"
        :to="item.ref"
        :value="item.ref"
        :prepend-icon="item.icon"
        :title="item.text"
      />
    </v-list>
    <template
      v-if="userStore.canUseEtl"
      #append
    >
      <v-list-item
        :to="swapRoute"
        :value="swapRoute"
        title="Swap to ETL"
        prepend-icon="mdi-swap-horizontal"
      />
    </template>
  </v-navigation-drawer>
  <v-navigation-drawer
    v-else
    permanent
    rail
    expand-on-hover
    :floating="!isEtlHovered"
    :border="isEtlHovered ? 't-sm' : ''"
    :rounded="isEtlHovered"
    @mouseenter="isEtlHovered = true"
    @mouseleave="isEtlHovered = false"
    @focus="isEtlHovered = true"
    @focusout="isEtlHovered = false"
  >
    <v-list
      :selected="[etlRoute]"
      nav
    >
      <v-list-item
        v-for="(etlNav, idx) in etlNavs"
        :key="idx"
        :to="etlNav.ref"
        :value="etlNav.ref"
        :title="etlNav.text"
        :prepend-icon="etlNav.icon"
      />
    </v-list>
    <template
      v-if="userStore.canUseGlyue"
      #append
    >
      <v-list-item
        :to="swapRoute"
        :value="swapRoute"
        title="Swap to Integration Gateway"
        prepend-icon="mdi-swap-horizontal"
      />
    </template>
  </v-navigation-drawer>
</template>

<script setup>
import { useInstallationStore } from "@/stores/installation"
import { useUserStore } from "@/stores/user"
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute, useRouter } from "vue-router"
import { getPreferenceValue } from '../preferences'

const router = useRouter()
const route = useRoute()
const keybindAdd = inject("glyue:keybindAdd")
const userStore = useUserStore()
const installationStore = useInstallationStore()
const currentPage = ref(-1)
const isEtl = computed(() => router.currentRoute.value.path.startsWith("/etl"))

// Hover state for ETL drawer
const isEtlHovered = ref(false)

const glyueNav = ref([
  { icon: "mdi-view-dashboard", text: "Dashboard", ref: "/dashboard" },
  { icon: "mdi-bookmark-multiple", text: "Bookmarks", ref: "/bookmarks" },
  { icon: "mdi-history", text: "Run History", ref: "/history" },
  { icon: "mdi-wrench", text: "Build", ref: "/build" },
  { icon: "mdi-tools", text: "Workbench", ref: "/workbench", workbenchEnabled: true },
  { icon: "mdi-map-outline", text: "Mapping", ref: "/mapping", pageKey: "mapping" },
  { icon: "mdi-code-block-braces", text: "Shared Code", ref: "/shared-code", permissions: ["can_use_shared_modules", "can_use_custom_adapters"] },
  { icon: "mdi-api", text: "Swagger API", ref: "/swagger" },
  { icon: "mdi-sitemap", text: "Frontends", ref: "/frontends", permissions: ["can_use_frontend", "view_frontend"] },
  { icon: "mdi-safe-square-outline", text: "Vault", ref: "/vault", vault: true },
  { icon: "mdi-calendar-clock", text: "Schedule", ref: "/schedules/dashboard" },
  { icon: "mdi-folder-move", text: "Migrate", ref: "/migrate", permissions: ["can_use_migrate"] },
  { icon: "mdi-book-open-page-variant-outline", text: "Catalog", ref: "/catalog", catalogEnabled: true },
  { icon: "mdi-file-compare", text: "View Changes", ref: "/diff", permissions: ["can_use_migrate"] },
  { icon: "mdi-text-long", text: "Log", ref: "/log", permissions: ["can_use_log"] },
  { icon: "mdi-account-plus", text: "Invite", ref: "/invite", permissions: ["can_use_invite"] },
])

const userNavigationLinks = computed(() => glyueNav.value.filter(v => {
  const pageOkay = !v.pageKey || installationStore.mapping_enabled !== false
  const adminOkay = !v.admin || (userStore.admin)
  const staffOkay = !v.staff || (userStore.staff || userStore.admin)
  const vaultOkay = !v.vault || userStore.vault
  const catalogOkay = !v.catalogEnabled || installationStore.catalogEnabled
  const workbenchOkay = !v.workbenchEnabled || installationStore.workbench_enabled
  const permissionsOkay = !v.permissions || v.permissions.some((permission) => userStore.hasPermission(permission))
  const navbarOkay = !userStore.navbar_items || userStore.navbar_items.includes(v.ref)

  return adminOkay && vaultOkay && catalogOkay && workbenchOkay && staffOkay && permissionsOkay && navbarOkay && pageOkay
}))

const etlNavs = ref([
  {
    icon: "mdi-sitemap",
    text: "My Workflows",
    ref: "/etl/workflows",
    startsWith: true,
  },{
    icon: "mdi-history",
    text: "Runs",
    ref: "/etl/runs"
  },
  {
    icon: "mdi-connection",
    text: "Data Connections",
    ref: "/etl/connections",
    startsWith: true,
  },
  {
    icon: "mdi-format-list-bulleted",
    text: "Value Mappings",
    ref: "/etl/value-mappings",
  },
  {
    icon: "mdi-account-plus",
    text: "Invite",
    ref: "/etl/invite",
  },

])

const etlRoute = computed(() => {
  const path = route.path

  const nav = etlNavs.value.find((nav) => {
    if (nav.startsWith) {
      return path.startsWith(nav.ref)
    } else {
      return path === nav.ref
    }
  })

  return nav ? nav.ref : null
})

const swapRoute = computed(() => {
  if (isEtl.value) {
    return `/${getPreferenceValue('landing-page')}` ?? '/dashboard'
  } else {
    return "/etl/workflows"
  }
})

onMounted(()=> {
  glyueNav.value.forEach((item) => {
    const page = item.ref.substring(1)

    keybindAdd(
      `nav-${page}`,
      () => router.push(item.ref),
      { metric: `nav/${page}`}
    )
  })

  keybindAdd(
    "nav-preferences",
    () => router.push("/preferences"),
    { metric: "nav/preferences" }
  )
})
</script>
